import { animalSplitPartHelper, AnimalSplitType } from '@farmshare/utils';
import { filter, flatMap, map, sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import {
  EnumProcessorSchedulingStatus,
  ProcessorSchedulingPaginationQuery,
  SortFindManyProcessorSchedulingInput,
  useProcessorSchedulingPaginationQuery,
} from 'lib/graphql';
import { userState } from 'state';

import {
  getProcessorSchedulingQueryFilters,
  shownStatuses,
} from 'pages/processor/dashboard/filters';

import { getQuestionnaireId, getSplitSlots } from '../shared/split-slot/utils';
import { matchSlotsToContacts } from '../utils/match-slots-to-contacts';

type PaginationItem = NonNullable<
  NonNullable<
    ProcessorSchedulingPaginationQuery['processorSchedulingPagination']
  >['items']
>[number];

type PaginationHead = NonNullable<
  NonNullable<PaginationItem['animalHeads']>[number]
>;

type PaginationCutsheetInfo = NonNullable<
  NonNullable<PaginationHead['cutsheetInformation']>[number]
>;

export type BuyerSlotInfo = {
  /** Index of the cutsheetInformation entry in head.cutsheetInformation */
  cutsheetInfoIndex: number;
  cutsheetInfoId: string;
  splitPart: string;
  splitLabel: string;
  cutsheetName?: string;
  cutsheetId?: string;
  locked: boolean;
  hasCutsheet: boolean;
  questionnaireId: string | null;
  questionnaireResponseId: string | null;
  /** The raw cutsheetInformation entry for passing to drawers */
  raw: PaginationCutsheetInfo;
};

export type BuyerOrderItem = {
  key: string;
  jobId: string;
  job: PaginationItem;
  head: PaginationHead;
  headIndex: number;
  producerName: string;
  processorName: string;
  vendorId: string;
  displayDate: string | null;
  animalSpecies: string;
  status: string;
  slots: BuyerSlotInfo[];
  allReady: boolean;
  needsAttention: boolean;
};

function getDisplayDate(
  job: PaginationItem,
  head: PaginationHead,
): string | null {
  return head.killDate ?? head.cutDate ?? null;
}

function buildBuyerSlots(
  head: PaginationHead,
  userId: string,
): BuyerSlotInfo[] {
  const splitType = head.splitType as AnimalSplitType | null | undefined;
  if (!splitType) return [];

  const slotDefs = getSplitSlots(splitType);
  const matched = matchSlotsToContacts(
    slotDefs,
    head.cutsheetInformation ?? [],
  );

  // Filter to only slots where this buyer is assigned
  return matched
    .map((m, i) =>
      m ? { info: m.match, slotDef: slotDefs[i], infoIndex: m.index } : null,
    )
    .filter((sc) => sc?.info.contactUser?._id === userId)
    .map((sc) => {
      const { info, slotDef, infoIndex } = sc!;
      const responseName =
        (info.questionnaireResponse as { name?: string } | null | undefined)
          ?.name ?? undefined;
      const responseId =
        (info.questionnaireResponse as { _id?: string } | null | undefined)
          ?._id ?? null;
      return {
        cutsheetInfoIndex: infoIndex,
        cutsheetInfoId: info._id ?? '',
        splitPart: slotDef.part,
        splitLabel:
          slotDefs.length > 1
            ? slotDef.label
            : animalSplitPartHelper(slotDef.part).label,
        cutsheetName: responseName ?? info.cutsheet?.name ?? undefined,
        cutsheetId: info.cutsheet?._id ?? undefined,
        locked: info.locked === true,
        hasCutsheet: !!info.cutsheet || !!responseId,
        questionnaireId: getQuestionnaireId(info.questionnaire),
        questionnaireResponseId: responseId,
        raw: info,
      };
    });
}

function flattenJobsToOrders(
  jobs: PaginationItem[],
  userId: string,
  excludeOwnJobs = false,
): BuyerOrderItem[] {
  return flatMap(jobs, (job) => {
    if (excludeOwnJobs && job.requestedBy?._id === userId) return [];
    return (job.animalHeads ?? [])
      .map((head, headIndex) => {
        if (!head) return null;
        const slots = buildBuyerSlots(head, userId);
        if (slots.length === 0) return null;

        const { requestedBy } = job;
        const producerName =
          [requestedBy?.first_name, requestedBy?.last_name]
            .filter(Boolean)
            .join(' ') ||
          requestedBy?.company ||
          '';

        return {
          key: `${job._id}-${head._id}`,
          jobId: job._id,
          job,
          head,
          headIndex,
          producerName,
          processorName: job.vendor?.shop_name ?? '',
          vendorId: job.vendor?._id ?? '',
          displayDate: getDisplayDate(job, head),
          animalSpecies: job.animalSpecies as string,
          status: job.status as string,
          slots,
          allReady: slots.every((s) => s.hasCutsheet),
          needsAttention: slots.some((s) => !s.hasCutsheet),
        } satisfies BuyerOrderItem;
      })
      .filter(Boolean) as BuyerOrderItem[];
  });
}

export function useBuyerOrders(options?: { excludeOwnJobs?: boolean }) {
  const excludeOwnJobs = options?.excludeOwnJobs ?? false;
  const user = useRecoilValue(userState);

  const {
    bookingsInProgress: activeFilter,
    bookingsCompleted: completedFilter,
  } = getProcessorSchedulingQueryFilters({
    customerId: user?._id,
    buyerOnly: true,
  });

  const activeFilterWithStatuses = {
    ...activeFilter,
    AND: [
      {
        OR: map(
          filter(
            shownStatuses,
            (s) =>
              (s as EnumProcessorSchedulingStatus) !==
              EnumProcessorSchedulingStatus.Completed,
          ),
          (s: string) => ({ status: s as EnumProcessorSchedulingStatus }),
        ),
      },
    ],
  };

  const defaultPaginationVars = {
    sort: SortFindManyProcessorSchedulingInput.DropoffdateAsc,
    page: 1,
    perPage: 50,
  };

  const activeQuery = useProcessorSchedulingPaginationQuery({
    fetchPolicy: 'network-only',
    skip: !user,
    variables: { ...defaultPaginationVars, filter: activeFilterWithStatuses },
  });

  const completedQuery = useProcessorSchedulingPaginationQuery({
    fetchPolicy: 'network-only',
    skip: !user,
    variables: { ...defaultPaginationVars, filter: completedFilter },
  });

  const activeOrders = useMemo(() => {
    const jobs =
      (activeQuery.data?.processorSchedulingPagination
        ?.items as PaginationItem[]) ?? [];
    const items = flattenJobsToOrders(jobs, user?._id ?? '', excludeOwnJobs);
    return sortBy(items, [
      (i) => (i.needsAttention ? 0 : 1),
      (i) => i.displayDate ?? '',
    ]);
  }, [activeQuery.data, user?._id, excludeOwnJobs]);

  const completedOrders = useMemo(() => {
    const jobs =
      (completedQuery.data?.processorSchedulingPagination
        ?.items as PaginationItem[]) ?? [];
    const items = flattenJobsToOrders(jobs, user?._id ?? '', excludeOwnJobs);
    return sortBy(items, [(i) => i.displayDate ?? '']);
  }, [completedQuery.data, user?._id, excludeOwnJobs]);

  const loading = activeQuery.loading || completedQuery.loading;

  const refetchOrders = useCallback(async () => {
    await Promise.all([activeQuery.refetch(), completedQuery.refetch()]);
  }, [activeQuery, completedQuery]);

  return { user, activeOrders, completedOrders, loading, refetchOrders };
}
